import React from 'react';

import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';

import { useQuery } from 'react-query';

import { fetchReclamationsSurBonDeVenteAdministration } from 'utils/ts-queries';
import { ApiReclamationSurBonDeVente } from 'types/apiReturns';

import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import DataObjectIcon from '@mui/icons-material/DataObject';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import GeswebLink from 'components/Shared/GeswebLink';
import JsonViewerDialog from 'components/Shared/JsonViewerDialog';
import TriStateFilter from 'components/Shared/TriStateFilter';
import Datetime from 'components/Shared/Datetime';
import ReclamationSurBonDeVenteDetail from 'components/Shared/ReclamationSurBonDeVenteDetail';

function ReclamationSurBonDeVenteRow(props: { item: ApiReclamationSurBonDeVente }) {
    const { item } = props;

    const [open, setOpen] = React.useState(false);
    const [jsonDialogOpen, setJsonDialogOpen] = React.useState(false);

    return (
        <>
            <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>
                    <GeswebLink cle={item.cle} structure="recent" mode="button" label={item.cle} environment={null} />
                    <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>

                <TableCell>
                    {item.reclamationId
                        ? <Link
                            to={`/reclamations/${item.reclamationId}`}
                            underline="hover"
                            component={RouterLink}
                            sx={{ fontWeight: 'bold' }}
                          >
                            {item.reclamationId}
                          </Link>
                        : '-'}
                </TableCell>

                <TableCell>{item.erreur}</TableCell>

                <TableCell><Datetime withTime datetime={item.dateCreation} /></TableCell>
                <TableCell>{item.dateIntegration ? <Datetime withTime datetime={item.dateIntegration} /> : '-'}</TableCell>
                <TableCell>{item.dateDerniereMiseAJour ? <Datetime withTime datetime={item.dateDerniereMiseAJour} /> : '-'}</TableCell>

                <TableCell>
                    <IconButton size="small" onClick={() => setJsonDialogOpen(true)} disabled={!item.messageJson}>
                        <DataObjectIcon />
                    </IconButton>
                </TableCell>
            </TableRow>

            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 1 }}>
                            <ReclamationSurBonDeVenteDetail value={item.messageJson} />
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            <JsonViewerDialog
                open={jsonDialogOpen}
                onClose={() => setJsonDialogOpen(false)}
                title={`Message JSON - Réclamation sur bon de vente ${item.cle}`}
                jsonEntries={[
                    { label: 'RECENT', value: item.messageJson },
                ]}
            />
        </>
    );
}

export default function AdministrationReclamationsSurBonDeVente() {

    const [page, setPage] = React.useState(0);
    const [nbParPage, setNbParPage] = React.useState(25);
    const [filtreCle, setFiltreCle] = React.useState('');
    const [filtreErreur, setFiltreErreur] = React.useState<boolean | null>(null);

    const { data, isLoading } = useQuery(
        ['reclamationsSurBonDeVenteAdministration', page, nbParPage, filtreCle, filtreErreur],
        () => fetchReclamationsSurBonDeVenteAdministration({
            page,
            nbParPage,
            filtreCle,
            filtreErreur,
            triPar: '',
            triAscendant: false,
        }),
        { keepPreviousData: true }
    );

    return (
        <div>
            <Typography variant="h6" gutterBottom>Réclamations sur bon de vente</Typography>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: '160px' }}>Clé</TableCell>
                            <TableCell sx={{ width: '110px' }}>Réclamation</TableCell>
                            <TableCell>Erreur</TableCell>
                            <TableCell sx={{ width: '150px' }}>Créé le</TableCell>
                            <TableCell sx={{ width: '150px' }}>Intégré le</TableCell>
                            <TableCell sx={{ width: '150px' }}>Dernière MAJ</TableCell>
                            <TableCell sx={{ width: '60px' }}>JSON</TableCell>
                        </TableRow>
                        <TableRow sx={{ margin: 0 }}>
                            <TableCell>
                                <TextField
                                    value={filtreCle}
                                    onChange={(e) => { setPage(0); setFiltreCle(e.target.value); }}
                                    size="small"
                                />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell>
                                <TriStateFilter
                                    value={filtreErreur}
                                    onChange={(v) => { setPage(0); setFiltreErreur(v); }}
                                    labels={['Tous', 'Sans erreur', 'En erreur']}
                                    maxWidth="160px"
                                />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data && data.reclamationsSurBonDeVente.map((item) => (
                            <ReclamationSurBonDeVenteRow key={item.id} item={item} />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={data ? data.nbFiltres : 0}
                page={page}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPage={nbParPage}
                onRowsPerPageChange={(e) => { setNbParPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
                labelRowsPerPage="Par page"
            />
        </div>
    );
}
